/**
 * Module dependencies.
 */

import isPropValid from '@emotion/is-prop-valid';
import { DehydratedState, HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GlobalStyle as GlobalStyleUntile } from '@untile/react-core/styles/global';
import { appWithTranslation } from 'next-i18next/pages';
import { generateDefaultSeo } from 'next-seo/pages';
import Head from 'next/head';
import { useState } from 'react';
import { Layout } from 'src/components/layout';
import { seoDefaultConfig } from 'src/core/constants/seo-default-config';
import { CooldownModalProvider } from 'src/providers/cooldown-modal';
import { SessionProvider } from 'src/providers/session';
import { ToastProvider } from 'src/providers/toast';
import { GlobalStyle } from 'src/styles/global';
import { AppProps } from 'src/types/app';
import { StyleSheetManager } from 'styled-components';

/**
 * Forward only valid html attributes to dom elements, like styled-components v5 did.
 */

function shouldForwardProp(propName: string, target: unknown) {
  return typeof target === 'string' ? isPropValid(propName) : true;
}

/**
 * `App` component.
 */

function App({ Component, pageProps }: AppProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            gcTime: 1000 * 60 * 15
          }
        }
      })
  );

  return (
    <>
      <Head>
        <meta content={'IE=edge'} httpEquiv={'X-UA-Compatible'} />

        <meta content={'text/html;charset=utf-8'} httpEquiv={'Content-Type'} />

        <meta content={'width=device-width, initial-scale=1'} name={'viewport'} />

        <meta content={'true'} name={'HandheldFriendly'} />

        <link href={'/favicon.ico'} rel={'shortcut icon'} type={'image/x-icon'} />

        <link href={'/favicons/apple-touch-icon.png'} rel={'apple-touch-icon'} sizes={'180x180'} />

        <link href={'/favicons/favicon-32x32.png'} rel={'icon'} sizes={'32x32'} type={'image/png'} />

        <link href={'/favicons/favicon-16x16.png'} rel={'icon'} sizes={'16x16'} type={'image/png'} />

        <link href={'/site.webmanifest'} rel={'manifest'} />

        <link color={'#0580bd'} href={'/favicons/safari-pinned-tab.svg'} rel={'mask-icon'} />

        <meta content={'#0580bd'} name={'msapplication-TileColor'} />

        <meta content={'#0580bd'} name={'theme-color'} />

        {generateDefaultSeo(seoDefaultConfig)}
      </Head>

      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <GlobalStyleUntile />

        <GlobalStyle />

        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={pageProps?.dehydratedState as DehydratedState}>
            <SessionProvider>
              <ToastProvider>
                <CooldownModalProvider>
                  <Layout isPrivate={pageProps?.isPrivate} layout={pageProps?.layout}>
                    <Component {...pageProps} />
                  </Layout>
                </CooldownModalProvider>
              </ToastProvider>
            </SessionProvider>
          </HydrationBoundary>

          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </StyleSheetManager>
    </>
  );
}

/**
 * Export `App` component.
 */

export default appWithTranslation(App);
